import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getMalwareDetectionGraphDetails as getMalwareDetailsService } from 'services/modules/soc/agent';

import ApexCharts from 'react-apexcharts';
import CircularProgress from '@mui/material/CircularProgress';
import { Card, CardContent, Box, Button, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVirus } from '@fortawesome/free-solid-svg-icons';


function MalwareDetectionGraph(props) {
    // React router
    const navigate = useNavigate();
    // Props
    const agentId = props.agentId;
    
    // State Variables
    const [chartData, setChartData] = useState({
        series: [{ name: 'Document Count', data: [] }],
        options: {
            chart: { type: 'bar', height: 350 },
            plotOptions: { bar: { columnWidth: '50%', distributed: false } },
            dataLabels: { enabled: false },
            xaxis: { categories: [], title: { text: 'Time Frame' } },
            yaxis: { title: { text: 'Count' } },
            legend: { show: false },
            tooltip: {
                y: { formatter: (val) => `${val} documents` },
            }
        }
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isDataEmpty, setIsDataEmpty] = useState(false);

    // Redux selector
    const selectedCompanyContainer = useSelector((state) => state.auth.companyData);

    // useMemo hook for memoizing computational values
    const endDate = useMemo(() => new Date(props.endDate), [props.endDate]);
    const startDate = useMemo(() => new Date(props.startDate), [props.startDate]);
    const socEnv = useMemo(() => selectedCompanyContainer?.soc_env, [selectedCompanyContainer]);

    // useRef hook
    const prevRequestRef = useRef(null);
    
    // Fetches Malware Graph Details based on agent id
    useEffect(() => {
        const fetchMalwareDetectionGraphDetails = async () => {
            const requestKey = `${agentId}-${socEnv}-${endDate}-${startDate}`;
            if (prevRequestRef.current === requestKey) return;
            prevRequestRef.current = requestKey;

            try {
                if (socEnv && endDate && startDate) {
                    setIsLoading(true);
                    let response = await getMalwareDetailsService({
                        agentId,
                        soc_env: socEnv,
                        end: endDate,
                        start: startDate
                    });

                    const rawData = response?.data?.rawResponse?.aggregations?.['2']?.buckets.map(item => {
                        const istDate = new Date(new Date(item.key_as_string).getTime() + 5.5 * 60 * 60 * 1000);
                        return { timestamp: istDate, count: item.doc_count };
                    });

                    const intervals = [];
                    const intervalDuration = (endDate - startDate) / 6;

                    for (let i = 0; i < 6; i++) {
                        const start = new Date(startDate.getTime() + i * intervalDuration);
                        const end = new Date(start.getTime() + intervalDuration);
                        intervals.push({ start, end, count: 0 });
                    }

                    rawData.forEach(item => {
                        const interval = intervals.find(interval =>
                            item.timestamp >= interval.start && item.timestamp < interval.end
                        );
                        if (interval) {
                            interval.count += item.count;
                        }
                    });

                    const categories = intervals.map(interval =>
                        `${interval.start.toLocaleDateString()} ${interval.start.getHours()}:00`
                    );
                    const counts = intervals.map(interval => interval.count);

                    setIsDataEmpty(counts.every(count => count === 0));
                    setChartData(prevState => ({
                        ...prevState,
                        series: [{ name: 'Document Count', data: counts }],
                        options: {
                            ...prevState.options,
                            xaxis: { categories: categories },
                            tooltip: {
                                y: { formatter: (val) => `${val} documents` },
                                x: {
                                    formatter: (val, { dataPointIndex }) => {
                                        const interval = intervals[dataPointIndex];
                                        return `${interval.start.toLocaleString()} - ${interval.end.toLocaleString()}`;
                                    }
                                }
                            }
                        }
                    }));
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMalwareDetectionGraphDetails();
    }, [agentId, socEnv, endDate, startDate]);

    return (
        <Card sx={{ margin: '10px', height: '450px' }}>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <div className="dailogBox">
                        <FontAwesomeIcon icon={faVirus} className="soc-icon"  />
                        <Typography variant="h6">Malicious Activities</Typography>
                    </div>
                    {!isDataEmpty && (
                        <Button
                            variant="contained"
                            onClick={() =>
                                navigate(`/soc/malware-data/?agentname=${props.agentName}&agentid=${agentId}`, { state: { end: endDate, start: startDate } })
                            }
                        >
                            View All
                        </Button>
                    )}
                </Box>
                <div className="soc-graph-container">
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <>
                            <ApexCharts
                                options={chartData.options}
                                series={chartData.series}
                                type="bar"
                                height={350}
                            />
                            {isDataEmpty && (
                                <div
                                    className="blur-effect"
                                >
                                    No Details Found
                                </div>
                            )}
                        </>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default MalwareDetectionGraph;
